// src/pages/lms/LMSSettings.jsx
import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useLMSAuth } from '../../hooks/useLMSAuth';
import LMSLayout from './LMSLayout';
import { Save, CheckCircle, AlertCircle } from 'lucide-react';

const COLORS = ['#4F8EF7','#7C3AED','#10B981','#F59E0B','#EF4444','#EC4899','#22D3EE','#0F172A'];

export default function LMSSettings() {
  const { account, institution, refreshAccount } = useLMSAuth();
  const [form,    setForm]    = useState({ contact_name:'', contact_email:'', contact_phone:'', cover_color:'#4F8EF7' });
  const [saving,  setSaving]  = useState(false);
  const [success, setSuccess] = useState('');
  const [error,   setError]   = useState('');

  useEffect(() => {
    if (account) {
      setForm({
        contact_name:  account.contact_name  || '',
        contact_email: account.contact_email || '',
        contact_phone: account.contact_phone || '',
        cover_color:   account.cover_color   || '#4F8EF7',
      });
    }
  }, [account]);

  const save = async () => {
    if (!form.contact_name.trim()) { setError('Contact name is required'); return; }
    setSaving(true); setError(''); setSuccess('');
    try {
      const { error: err } = await supabase
        .from('institution_accounts')
        .update({
          contact_name:  form.contact_name.trim(),
          contact_phone: form.contact_phone.trim(),
          cover_color:   form.cover_color,
          updated_at:    new Date().toISOString(),
        })
        .eq('id', account.id);
      if (err) throw err;
      await refreshAccount();
      setSuccess('Settings saved successfully!');
      setTimeout(() => setSuccess(''), 4000);
    } catch(e) {
      setError(e.message || 'Failed to save. Please try again.');
    } finally { setSaving(false); }
  };

  const inp = { width:'100%', padding:'11px 14px', borderRadius:10, border:'1.5px solid #E5E7EB', fontSize:14, boxSizing:'border-box', outline:'none', color:'#111827' };
  const label = { fontSize:13, fontWeight:600, color:'#374151', display:'block', marginBottom:6 };

  return (
    <LMSLayout>
      <div style={{ marginBottom:24 }}>
        <h1 style={{ fontSize:22, fontWeight:800, color:'#111827', margin:0 }}>Settings</h1>
        <p style={{ color:'#6B7280', fontSize:13, margin:'4px 0 0' }}>Manage your institution's LMS portal settings</p>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 360px', gap:20, alignItems:'start' }}>
        <div style={{ display:'flex', flexDirection:'column', gap:20 }}>

          {/* Institution Info (readonly) */}
          <div style={{ background:'#fff', borderRadius:16, border:'1px solid #E5E7EB', overflow:'hidden' }}>
            <div style={{ padding:'16px 20px', borderBottom:'1px solid #E5E7EB', fontWeight:800, color:'#111827', fontSize:14 }}>
              Institution Information
            </div>
            <div style={{ padding:20 }}>
              {[
                ['Institution Name', institution?.name      || '—'],
                ['Category',         institution?.category  || '—'],
                ['City',             institution?.city      || '—'],
                ['Country',          institution?.country   || '—'],
                ['LMS Username',    `@${account?.lms_username || '—'}`],
                ['Account Status',   account?.is_active ? '✅ Active & Verified' : '⏳ Pending Approval'],
              ].map(([lbl, val]) => (
                <div key={lbl} style={{ display:'flex', justifyContent:'space-between', padding:'10px 0', borderBottom:'1px solid #F3F4F6', fontSize:13 }}>
                  <span style={{ color:'#6B7280', fontWeight:600 }}>{lbl}</span>
                  <span style={{ color:'#111827', fontWeight:500, textAlign:'right', maxWidth:'60%' }}>{val}</span>
                </div>
              ))}
              <p style={{ fontSize:12, color:'#9CA3AF', marginTop:12, margin:'12px 0 0' }}>
                Institution details cannot be changed here. Contact admin@inncatechnologies.com to update them.
              </p>
            </div>
          </div>

          {/* Contact Settings */}
          <div style={{ background:'#fff', borderRadius:16, border:'1px solid #E5E7EB', overflow:'hidden' }}>
            <div style={{ padding:'16px 20px', borderBottom:'1px solid #E5E7EB', fontWeight:800, color:'#111827', fontSize:14 }}>
              Contact Information
            </div>
            <div style={{ padding:20 }}>
              {success && (
                <div style={{ background:'#F0FDF4', border:'1px solid #BBF7D0', borderRadius:10, padding:'10px 14px', marginBottom:16, display:'flex', gap:8, alignItems:'center' }}>
                  <CheckCircle size={15} color="#10B981" />
                  <span style={{ fontSize:13, color:'#065F46', fontWeight:600 }}>{success}</span>
                </div>
              )}
              {error && (
                <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:10, padding:'10px 14px', marginBottom:16, display:'flex', gap:8, alignItems:'center' }}>
                  <AlertCircle size={15} color="#EF4444" />
                  <span style={{ fontSize:13, color:'#991B1B' }}>{error}</span>
                </div>
              )}

              <div style={{ marginBottom:16 }}>
                <label style={label}>Contact Person Name *</label>
                <input value={form.contact_name} onChange={e=>setForm({...form,contact_name:e.target.value})} placeholder="e.g. Dr. Ahmed Ali" style={inp} />
              </div>
              <div style={{ marginBottom:16 }}>
                <label style={label}>Login Email (cannot change)</label>
                <input value={form.contact_email} disabled style={{ ...inp, background:'#F9FAFB', color:'#6B7280', cursor:'not-allowed' }} />
                <p style={{ fontSize:11, color:'#9CA3AF', margin:'4px 0 0' }}>Contact admin to change login email</p>
              </div>
              <div style={{ marginBottom:20 }}>
                <label style={label}>Contact Phone</label>
                <input value={form.contact_phone} onChange={e=>setForm({...form,contact_phone:e.target.value})} placeholder="e.g. +92 300 1234567" style={inp} />
              </div>

              <div style={{ marginBottom:20 }}>
                <label style={label}>Portal Accent Color</label>
                <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                  {COLORS.map(c => (
                    <div key={c} onClick={() => setForm({...form,cover_color:c})}
                      style={{ width:32, height:32, borderRadius:'50%', background:c, cursor:'pointer', border: form.cover_color===c ? '3px solid #111827' : '3px solid transparent', transition:'border 0.15s' }} />
                  ))}
                </div>
              </div>

              <button onClick={save} disabled={saving}
                style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:8, width:'100%', padding:'12px 0', borderRadius:12, border:'none', background: saving ? '#E5E7EB' : 'linear-gradient(135deg,#4F8EF7,#7C3AED)', color: saving ? '#9CA3AF' : '#fff', fontSize:14, fontWeight:700, cursor: saving ? 'wait' : 'pointer' }}>
                <Save size={16} /> {saving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>
        </div>

        {/* Right panel — Password change */}
        <div style={{ background:'#fff', borderRadius:16, border:'1px solid #E5E7EB', overflow:'hidden' }}>
          <div style={{ padding:'16px 20px', borderBottom:'1px solid #E5E7EB', fontWeight:800, color:'#111827', fontSize:14 }}>
            Change Password
          </div>
          <div style={{ padding:20 }}>
            <PasswordChange />
          </div>
        </div>
      </div>
    </LMSLayout>
  );
}

function PasswordChange() {
  const [newPwd,   setNewPwd]   = useState('');
  const [confirm,  setConfirm]  = useState('');
  const [saving,   setSaving]   = useState(false);
  const [success,  setSuccess]  = useState('');
  const [error,    setError]    = useState('');

  const save = async () => {
    if (newPwd.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (newPwd !== confirm) { setError('Passwords do not match'); return; }
    setSaving(true); setError(''); setSuccess('');
    try {
      const { error: err } = await supabase.auth.updateUser({ password: newPwd });
      if (err) throw err;
      setSuccess('Password updated successfully!');
      setNewPwd(''); setConfirm('');
      setTimeout(() => setSuccess(''), 5000);
    } catch(e) {
      setError(e.message || 'Failed to update password');
    } finally { setSaving(false); }
  };

  const inp = { width:'100%', padding:'11px 14px', borderRadius:10, border:'1.5px solid #E5E7EB', fontSize:14, boxSizing:'border-box', outline:'none' };

  return (
    <div>
      {success && <div style={{ background:'#F0FDF4', border:'1px solid #BBF7D0', borderRadius:8, padding:'10px 12px', marginBottom:14, fontSize:13, color:'#065F46', fontWeight:600 }}>{success}</div>}
      {error   && <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:8, padding:'10px 12px', marginBottom:14, fontSize:13, color:'#991B1B' }}>{error}</div>}
      <div style={{ marginBottom:14 }}>
        <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:5 }}>New Password</label>
        <input type="password" value={newPwd} onChange={e=>setNewPwd(e.target.value)} placeholder="Min 8 characters" style={inp} />
      </div>
      <div style={{ marginBottom:20 }}>
        <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:5 }}>Confirm Password</label>
        <input type="password" value={confirm} onChange={e=>setConfirm(e.target.value)} placeholder="Repeat password" style={inp} />
      </div>
      <button onClick={save} disabled={saving}
        style={{ width:'100%', padding:'11px 0', borderRadius:10, border:'none', background: saving ? '#E5E7EB' : '#EF4444', color: saving ? '#9CA3AF' : '#fff', fontSize:13, fontWeight:700, cursor: saving ? 'wait' : 'pointer' }}>
        {saving ? 'Updating...' : 'Update Password'}
      </button>
    </div>
  );
}